import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import BackToDashboard from '@/components/BackToDashboard';
import { api } from '@/lib/api';
import { useLang } from '@/context/LangContext';
import { BADGE_I18N } from '@/lib/badges';
import { Bell, Check, ShoppingCart, Star, UserPlus, Settings as SettingsIcon, ChevronRight, BellRing, Zap, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { isPushSupported, getCurrentSubscription, subscribePush, unsubscribePush, sendTestPush } from '@/lib/push';

const ICONS = { purchase: ShoppingCart, review: Star, invite_used: UserPlus, system: SettingsIcon, achievement: Trophy };

// Achievement-unlock localized prefix
const ACHIEVEMENT_PREFIX = {
  es: '🏆 Logro desbloqueado: ',
  en: '🏆 Achievement unlocked: ',
  fr: '🏆 Trophée débloqué : ',
  pt: '🏆 Conquista desbloqueada: ',
  zh: '🏆 已解锁成就：',
  ar: '🏆 تم فتح الإنجاز: ',
};

function localizeNotification(n, lang) {
  // Achievement-unlock notifications carry meta.badge_id — re-render title+body from the local catalog
  const bid = n.meta?.badge_id;
  if (bid && BADGE_I18N[lang]?.[bid]) {
    const b = BADGE_I18N[lang][bid];
    return {
      ...n,
      title: `${ACHIEVEMENT_PREFIX[lang] || ACHIEVEMENT_PREFIX.es}${b.name}`,
      body: b.desc,
      _icon: 'achievement',
    };
  }
  return n;
}

export default function Notifications() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pushSub, setPushSub] = useState(null);
  const [pushSupported, setPushSupported] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/notifications');
      setItems(r.data.notifications || []);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    (async () => {
      const sup = await isPushSupported();
      setPushSupported(sup);
      if (sup) setPushSub(await getCurrentSubscription());
    })();
  }, []);

  const markAllRead = async () => {
    await api.post('/notifications/read-all');
    setItems(it => it.map(n => ({ ...n, read: true })));
    toast.success(t('notif_marked_all'));
    window.dispatchEvent(new Event('pm:notifications-updated'));
  };

  const markRead = async (id) => {
    await api.post(`/notifications/read/${id}`);
    setItems(it => it.map(n => n.id === id ? { ...n, read: true } : n));
    window.dispatchEvent(new Event('pm:notifications-updated'));
  };

  const enablePush = async () => {
    setBusy(true);
    try {
      await subscribePush();
      setPushSub(await getCurrentSubscription());
      toast.success(t('notif_push_on'));
    } catch (e) { toast.error(e.message || t('notif_push_error')); }
    finally { setBusy(false); }
  };
  const disablePush = async () => {
    setBusy(true);
    try { await unsubscribePush(); setPushSub(null); toast.success(t('notif_push_off')); }
    catch { toast.error(t('error_generic')); } finally { setBusy(false); }
  };
  const testPush = async () => {
    try {
      const r = await sendTestPush();
      const sent = r.data.sent;
      toast.success(`${t('notif_test_sent')} (${sent} ${sent === 1 ? t('devices') : t('devices_plural')}).`);
    } catch { toast.error(t('notif_test_error')); }
  };

  return (
    <Layout>
      <BackToDashboard />
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-gold mb-2">{t('notifications_eyebrow')}</div>
          <h1 className="font-h uppercase text-4xl sm:text-5xl flex items-center gap-3">
            <Bell className="w-9 h-9 text-gold" /> {t('notifications_h1')}
          </h1>
        </div>
        <div className="flex gap-2">
          {pushSupported && !pushSub && (
            <button data-testid="enable-push" disabled={busy} onClick={enablePush}
              className="px-4 py-2 rounded-md bg-gold text-navy font-h uppercase tracking-wider text-sm font-semibold hover:bg-gold-light inline-flex items-center gap-1">
              <BellRing className="w-4 h-4" /> {t('notif_enable_push')}
            </button>
          )}
          {pushSub && (
            <>
              <button data-testid="test-push" onClick={testPush}
                className="px-3 py-2 rounded-md border border-gold/40 text-gold hover:bg-gold/10 font-h uppercase tracking-wider text-xs inline-flex items-center gap-1">
                <Zap className="w-3 h-3" /> {t('notif_test')}
              </button>
              <button data-testid="disable-push" disabled={busy} onClick={disablePush}
                className="px-3 py-2 rounded-md border border-alert/50 text-alert hover:bg-alert/10 font-h uppercase tracking-wider text-xs">
                {t('notif_disable_push')}
              </button>
            </>
          )}
          <button data-testid="mark-all-read" onClick={markAllRead}
            className="px-4 py-2 rounded-md bg-gold text-navy font-h uppercase tracking-wider text-sm font-semibold hover:bg-gold-light inline-flex items-center gap-1">
            <Check className="w-4 h-4" /> {t('notif_mark_all')}
          </button>
        </div>
      </div>

      {loading && <div className="text-gray-400">{t('loading')}</div>}

      <div className="space-y-2">
        {items.map(raw => {
          const n = localizeNotification(raw, lang);
          const Icon = ICONS[n._icon || n.kind] || Bell;
          const Comp = n.link ? Link : 'div';
          const props = n.link ? { to: n.link } : {};
          return (
            <Comp key={n.id} {...props} onClick={() => !n.read && markRead(n.id)}
              data-testid={`notif-${n.id}`}
              className={`flex items-start gap-4 p-4 rounded-lg border transition hover-lift ${n.read ? 'bg-card-steel border-steel' : 'bg-gold/5 border-gold/50'}`}>
              <div className={`w-10 h-10 rounded-md border flex items-center justify-center flex-shrink-0 ${n.read ? 'border-steel text-gray-400' : 'border-gold text-gold bg-gold/10'}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`font-h uppercase ${n.read ? 'text-gray-300' : 'text-white'}`}>{n.title}</div>
                <p className="text-sm text-gray-300 mt-0.5">{n.body}</p>
                <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">{n.created_at?.slice(0, 16).replace('T', ' ')}</div>
              </div>
              {!n.read && <div className="w-2 h-2 rounded-full bg-gold mt-2" />}
              {n.link && <ChevronRight className="w-4 h-4 text-gold mt-1" />}
            </Comp>
          );
        })}
        {!loading && !items.length && (
          <div className="text-center text-gray-400 py-12 border-2 border-dashed border-gold/30 rounded-lg">
            {t('notif_empty')}
          </div>
        )}
      </div>
    </Layout>
  );
}
